import React, { FC, memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { navigate } from "@reach/router";
import { WaitingButton, IconButton, Flex, Description, FlatButton } from "@evergis/ui";

import {
  LoginWithContainer,
  SendConfirmEmailButton,
  SignInFormContainer,
  SignInFormHeader,
  SignInTitle,
  SocialNetworks,
} from "./styled";
import { FormInputs } from "./FormInputs";
import { Link, ExternalLink } from "../../components/styled/links/Link";
import { ButtonsContainer, Description as Desc, ErrorMessage } from "../../components/styled/forms";
import { TermsDescription } from "../sign-up/styled";

import { ROUTES } from "appConfig";

import { getEmailNotConfirmed } from "../../ducks/user";
import { useLoginFormSettings } from "../../hooks/useLoginFormSettings";
import { getPortalConfigSelector } from "../../ducks/portalConfig";
import { getMarkdownString } from "../../utils/string";
import { enhance } from "./enhance";

import { api } from "../../api/api";

import { SignInProps } from "./types";

// eslint-disable-next-line max-lines-per-function
const SignInComponent: FC<SignInProps> = ({ error, onSignIn, resetSuccessError }) => {
  const { t } = useTranslation();
  const config = useSelector(getPortalConfigSelector);
  const emailNotConfirmed = useSelector(getEmailNotConfirmed);
  const [loginInput, setLoginInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [isWaiting, setIsWaiting] = useState(false);

  const {
    title,
    login,
    password,
    loginWith,
    terms,
    button,
    remember,
    signUp,
    allowRegistration,
    allowSNS,
    allowRecovery,
    showTermsOfService,
    disabledComponents,
  } = useLoginFormSettings();

  const send = useCallback(() => {
    if (loginInput && passwordInput) {
      setIsWaiting(true);

      onSignIn?.({ username: loginInput, password: passwordInput }).finally(() => {
        setIsWaiting(false);
        setPasswordInput("");
      });
    }
  }, [loginInput, passwordInput, onSignIn]);

  const sendConfirmEmail = useCallback(async () => {
    resetSuccessError && resetSuccessError();
    await api.account.verifyEmail(loginInput);
    navigate(ROUTES.ACCESS_RECOVERY_SENT);
  }, [loginInput]);

  if (config === undefined) {
    return null;
  }

  const hasTerms = showTermsOfService === undefined || showTermsOfService;
  const hasRegistration = allowRegistration === undefined || allowRegistration;
  const hasSNS = hasRegistration && (allowSNS === undefined || allowSNS);
  const hasRecovery = allowRecovery === undefined || allowRecovery;

  return (
    <SignInFormContainer>
      <SignInFormHeader>
        <SignInTitle>{title}</SignInTitle>
      </SignInFormHeader>
      <FormInputs
        send={send}
        error={error}
        login={login as string}
        password={password as string}
        loginInput={loginInput}
        passwordInput={passwordInput}
        setLogin={setLoginInput}
        setPassword={setPasswordInput}
      />
      <ButtonsContainer style={{ marginBottom: "1rem" }}>
        {hasRegistration && (
          <Link to={ROUTES.SIGN_UP} $description>
            <FlatButton primary>{signUp}</FlatButton>
          </Link>
        )}
        <WaitingButton primary onClick={send} isWaiting={isWaiting} className="primary-button">
          {button}
        </WaitingButton>
      </ButtonsContainer>
      {hasTerms && <TermsDescription dangerouslySetInnerHTML={{ __html: getMarkdownString(terms) }} />}
      {hasRecovery && (
        <Desc>
          <Link to={ROUTES.ACCESS_RECOVERY}>{remember}</Link>
        </Desc>
      )}
      {hasSNS && (
        <SocialNetworks>
          <LoginWithContainer>{loginWith}</LoginWithContainer>
          <Flex>
            {!disabledComponents?.google && (
              <ExternalLink href={api.external.login("google")}>
                <IconButton kind={"social_google"} />
              </ExternalLink>
            )}
            {!disabledComponents?.facebook && (
              <ExternalLink href={api.external.login("facebook")}>
                <IconButton kind={"social_fb"} />
              </ExternalLink>
            )}
            {!disabledComponents?.vk && (
              <ExternalLink href={api.external.login("vk")}>
                <IconButton kind={"social_vk"} />
              </ExternalLink>
            )}
            {!disabledComponents?.yandex && (
              <ExternalLink href={api.external.login("yandex")}>
                <IconButton kind={"social_yandex"} />
              </ExternalLink>
            )}
          </Flex>
        </SocialNetworks>
      )}
      {!!error && (
        <ErrorMessage>
          <Description style={{ margin: 0 }}>
            {typeof error !== "string" ? error?.non_field_errors : error}
            {emailNotConfirmed && ` ${loginInput}`}
          </Description>
        </ErrorMessage>
      )}
      {!!emailNotConfirmed && (
        <SendConfirmEmailButton onClick={sendConfirmEmail}>{t("sendConfirmEmail")}</SendConfirmEmailButton>
      )}
    </SignInFormContainer>
  );
};

export const Form = enhance(memo(SignInComponent));
